import { useMutation, useQuery } from '@tanstack/react-query'
import AXIOS_INSTANCE from '../api/axiosInstance'
import { queryClient } from '../store/provider/TanStackProvider'
import { AdmissionSummaryDataMock, AdmissionSummaryResMock } from '../types/admissionSummary.type'
import { EncounterArgOptions, EncounterDataMock, EncounterResMock } from '../types/encounter.type'

type Status = {
  status: 'verified' | 'in_consultation' | 'ongoing' | 'completed' | 'review_requested'
}

export const useEncounter = (arg?: EncounterArgOptions) => {
  const page = arg?.page
  const limit = arg?.limit
  const encounter_type = arg?.encounter_type
  const patient_id = arg?.patient_id
  const id = arg?.id
  const status = arg?.status
  const sort_dir = arg?.sort_dir || 'desc'
  const sort_by = arg?.sort_by || 'created_at'
  const appointed_staff_id = arg?.appointed_staff_id

  const { data: encounters, isLoading: encountersIsLoading } = useQuery({
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get<EncounterResMock<EncounterDataMock[]>>('encounters', {
        params: {
          ...(appointed_staff_id && { appointed_staff_id }),
          ...(patient_id && { patient_id }),
          ...(page && { 'page[number]': page }),
          ...(limit && { 'page[size]': limit }),
          ...(sort_by && { sort_by }),
          ...(sort_dir && { sort_dir }),
          ...(status && { status }),
          ...(encounter_type && { encounter_type })
        }
      })
      return data
    },
    queryKey: ['encounters', { appointed_staff_id, encounter_type, limit, page, patient_id, sort_by, sort_dir, status }]
  })

  const { data: singleEncounterRes, isLoading: singleEncounterIsLoading } = useQuery({
    enabled: !!id,
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get<EncounterResMock<EncounterDataMock>>(`encounters/${id}`)
      return data
    },
    queryKey: ['encounters', { id }]
  })

  const { data: admissionSummaryResponse, isLoading: admissionSummaryIsLoading } = useQuery({
    enabled: !!id,
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get<AdmissionSummaryResMock<AdmissionSummaryDataMock>>(
        `encounters/${id}/admission`
      )
      return data
    },
    queryKey: ['admission_summary', { id }]
  })

  const { mutateAsync: addEncounter, isPending: addEncounterIsPending } = useMutation({
    mutationFn: async (formVal: any) => {
      const { data } = await AXIOS_INSTANCE.post('encounters', formVal, {
        headers: { 'Content-Type': 'application/vnd.api+json' }
      })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['encounters'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
      queryClient.invalidateQueries({ queryKey: ['intake'] })
    }
  })
  const { mutateAsync: updateEncounter } = useMutation({
    mutationFn: async ({ formVal, id }: { formVal: any; id: string }) => {
      const { data } = await AXIOS_INSTANCE.patch(`encounters/${id}`, formVal, {
        headers: { 'Content-Type': 'application/vnd.api+json' }
      })
      return data
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['encounters'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
      queryClient.invalidateQueries({ queryKey: ['encounters', { id }] })
      queryClient.invalidateQueries({ queryKey: ['intake'] })
    }
  })

  const { mutateAsync: updateStatus, isPending } = useMutation({
    mutationFn: async ({ formVal, id }: { formVal: Status; id: string }) => {
      const { data } = await AXIOS_INSTANCE.patch(`encounters/${id}/update_status`, formVal)
      return data
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['encounters'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
      queryClient.invalidateQueries({ queryKey: ['encounters', { id }] })
    }
  })

  const { mutateAsync: reopenEncounter, isPending: reopenEncounterIsPending } = useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
      const { data } = await AXIOS_INSTANCE.patch(
        `encounters/${id}/reopen`,
        { reason },
        {
          headers: {
            Accept: 'application/vnd.api+json',
            'Content-Type': 'application/json'
          }
        }
      )
      return data
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['encounters'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
      queryClient.invalidateQueries({ queryKey: ['encounters', { id }] })
      queryClient.invalidateQueries({ queryKey: ['intake'] })
    }
  })

  const { mutateAsync: deleteEncounter } = useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      const { data } = await AXIOS_INSTANCE.delete(`encounters/${id}`, {
        headers: { 'Content-Type': 'application/vnd.api+json' }
      })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['encounters'] })
    }
  })

  return {
    addEncounter,
    addEncounterIsPending,
    admissionSummary: admissionSummaryResponse?.data,
    admissionSummaryIsLoading,
    deleteEncounter,
    encounters: encounters?.data,
    encountersIsLoading,
    includedAdmissionSummary: admissionSummaryResponse?.included,
    includedDatas: encounters?.included,
    isPending,
    pagination: encounters?.meta.pagination,
    reopenEncounter,
    reopenEncounterIsPending,
    singleEncounter: singleEncounterRes?.data,
    singleEncounterIncluded: singleEncounterRes?.included,
    singleEncounterIsLoading,
    updateEncounter,
    updateStatus
  }
}
